import { clientEnv } from "@/config";
import {
  IBanks,
  IGeneralData,
  IGetPaidBillDetailsParams,
  IGetPurchasedInquiryDetailsParams,
  IIncreaseWalletDepositPayload,
  IIncreaseWalletDepositRes,
  IInquiryValidityPayload,
  IPurchasePackagePayload,
  IRawInquiriesResData,
  IRawInquiryItem,
  IRawInquiryItemByBank,
  IRawInquiryValidityResData,
  IRawPackagesResData,
  IRawPaidBillResData,
  IRawPaidBillsItem,
  IRawPurchasedPackagesResData,
  IRawReceiptResData,
  IRawSettingsResData,
  IRawStoredDataRes,
  IRawWalletBalanceRes,
  IRawWalletTransactionsRes,
  ITokenBase,
  ITokenBaseQueryParams,
  IWithdrawWalletDepositPayload,
  TInquiriesDetailsData,
} from "@/interfaces";
import { checkIsToken, fetcher } from "@/lib";
import { apiRoutes } from "@/routes/routes";
import { IPayFinesPayload } from "@/interfaces/inquiries/vehicle";
import { IMyBillsResponse, ISavedBill } from "@/lib/myBillsStorage";

class CoreClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async getInquiry({
    inquiry,
    token,
  }: {
    inquiry: string;
    token?: string;
  }): Promise<IGeneralData<IRawInquiryItem>> {
    const url = `${apiRoutes.core.GET_INQUIRY}/${inquiry}`;

    const data = await fetcher<IGeneralData<IRawInquiryItem>>({
      url,
      token,
      // Service configuration drives SEO content; do not let server fetch
      // caching delay an administrator's published changes.
      options: { cache: "no-store" },
    });

    return data;
  }

  async getInquiryByBank({
    inquiry,
    bank,
  }: {
    inquiry: string;
    bank: string;
  }): Promise<IGeneralData<IRawInquiryItemByBank>> {
    const url = `${apiRoutes.core.GET_INQUIRY}/${inquiry}/${bank}`;
    const data = await fetcher<IGeneralData<IRawInquiryItemByBank>>({
      url,
    });

    return data;
  }

  async getInquiries(): Promise<IGeneralData<IRawInquiriesResData>> {
    const url = apiRoutes.core.GET_INQUIRY;

    const data = await fetcher<IGeneralData<IRawInquiriesResData>>({
      url,
    });
    return data;
  }

  async getSettings(
    token?: string,
  ): Promise<IGeneralData<IRawSettingsResData>> {
    checkIsToken(token);
    const url = apiRoutes.core.SETTINGS;

    const data = await fetcher<IGeneralData<IRawSettingsResData>>({
      url,
      token,
    });
    return data;
  }

  async getHomePageBanners(): Promise<
    IGeneralData<
      {
        id: number;
        title: string;
        alt: string;
        link: string;
        image_url: string;
        sort_order: number;
      }[]
    >
  > {
    const data = await fetcher<
      IGeneralData<
        {
          id: number;
          title: string;
          alt: string;
          link: string;
          image_url: string;
          sort_order: number;
        }[]
      >
    >({
      url: apiRoutes.core.HOME_PAGE_BANNERS,
      options: { cache: "no-store" },
    });

    return data;
  }

  async getWalletBalance({
    token,
  }: ITokenBase): Promise<IGeneralData<IRawWalletBalanceRes>> {
    checkIsToken(token);
    const url = apiRoutes.core.GET_WALLET_BALANCE;
    const data = await fetcher<IGeneralData<IRawWalletBalanceRes>>({
      url,
      token,
    });

    return data;
  }

  async increaseWalletDeposit({
    token,
    payload,
    bank,
  }: {
    token: string | undefined;
    payload: IIncreaseWalletDepositPayload;
    bank?: string;
  }): Promise<IGeneralData<IIncreaseWalletDepositRes>> {
    checkIsToken(token);
    const url = `${apiRoutes.core.WALLET_DEPOSIT}?bank=${bank}`;

    const data = await fetcher<
      IGeneralData<IIncreaseWalletDepositRes>,
      IIncreaseWalletDepositPayload
    >({
      url,
      token,
      payload,
      method: "POST",
    });

    return data;
  }

  async payFines({
    token,
    payload,
  }: {
    token: string | undefined;
    payload: IPayFinesPayload;
  }): Promise<IGeneralData<IIncreaseWalletDepositRes>> {
    checkIsToken(token);
    const url = apiRoutes.core.PAY_FINES;

    const data = await fetcher<
      IGeneralData<IIncreaseWalletDepositRes>,
      IPayFinesPayload
    >({
      url,
      token,
      payload,
      method: "POST",
    });

    return data;
  }

  async payFinesTokenless({
    payload,
  }: {
    payload: IPayFinesPayload;
  }): Promise<IGeneralData<IIncreaseWalletDepositRes>> {
    const url = apiRoutes.core.PAY_FINES_TOKENLESS;

    const data = await fetcher<
      IGeneralData<IIncreaseWalletDepositRes>,
      IPayFinesPayload
    >({
      url,
      payload,
      method: "POST",
    });

    return data;
  }

  async payGateFee({
    token,
    payload,
  }: {
    token?: string;
    payload: {
      purchase_id?: number | string;
      inquery_id?: number | string;
      flag?: string;
    };
  }): Promise<IGeneralData> {
    checkIsToken(token);
    return fetcher<IGeneralData, typeof payload>({
      url: apiRoutes.core.GATE_FEE,
      token,
      payload,
      method: "POST",
    });
  }

  async getStoredData({
    token,
    query,
  }: ITokenBaseQueryParams): Promise<IGeneralData<IRawStoredDataRes>> {
    checkIsToken(token);
    const url = apiRoutes.core.STORED_DATA;

    const data = await fetcher<IGeneralData<IRawStoredDataRes>>({
      url,
      token,
      params: query,
    });

    return data;
  }

  async getWalletTransactions({
    token,
    query,
  }: ITokenBaseQueryParams): Promise<IGeneralData<IRawWalletTransactionsRes>> {
    checkIsToken(token);
    const url = apiRoutes.core.WALLET_TRANSACTIONS;

    const data = await fetcher<IGeneralData<IRawWalletTransactionsRes>>({
      url,
      token,
      params: query,
    });
    return data;
  }

  async getPaidBillsHistory({
    token,
    query,
  }: ITokenBaseQueryParams): Promise<IGeneralData<IRawPaidBillsItem[]>> {
    checkIsToken(token);
    const url = apiRoutes.core.PAID_BILLS_HISTORY;

    const data = await fetcher<IGeneralData<IRawPaidBillsItem[]>>({
      url,
      token,
      params: query,
    });
    return data;
  }

  async getMyBills({
    token,
    query,
  }: ITokenBaseQueryParams): Promise<IGeneralData<IMyBillsResponse>> {
    checkIsToken(token);
    return fetcher<IGeneralData<IMyBillsResponse>>({
      url: apiRoutes.core.MY_BILLS,
      token,
      params: query,
    });
  }

  async createMyBill({
    token,
    payload,
  }: {
    token?: string;
    payload: {
      purchase_id: number;
      label?: string;
      reminder_enabled?: boolean;
      reminder_day?: number;
      reminder_interval?: "monthly" | "bimonthly";
    };
  }): Promise<IGeneralData<ISavedBill>> {
    checkIsToken(token);
    return fetcher<IGeneralData<ISavedBill>, typeof payload>({
      url: apiRoutes.core.MY_BILLS,
      token,
      method: "POST",
      payload,
    });
  }

  async updateMyBill({
    token,
    billId,
    payload,
  }: {
    token?: string;
    billId: string;
    payload: Partial<
      Pick<
        ISavedBill,
        "label" | "reminderEnabled" | "reminderDay" | "reminderInterval"
      >
    >;
  }): Promise<IGeneralData<ISavedBill>> {
    checkIsToken(token);
    return fetcher<
      IGeneralData<ISavedBill>,
      {
        label?: string;
        reminder_enabled?: boolean;
        reminder_day?: number | null;
        reminder_interval?: "monthly" | "bimonthly" | null;
      }
    >({
      url: `${apiRoutes.core.MY_BILLS}/${billId}`,
      token,
      method: "PUT",
      payload: {
        ...(payload.label !== undefined && { label: payload.label }),
        ...(payload.reminderEnabled !== undefined && {
          reminder_enabled: payload.reminderEnabled,
        }),
        ...(payload.reminderDay !== undefined && {
          reminder_day: payload.reminderDay,
        }),
        ...(payload.reminderInterval !== undefined && {
          reminder_interval: payload.reminderInterval,
        }),
      },
    });
  }

  async deleteMyBill({
    token,
    billId,
  }: {
    token?: string;
    billId: string;
  }): Promise<IGeneralData> {
    checkIsToken(token);
    return fetcher<IGeneralData>({
      url: `${apiRoutes.core.MY_BILLS}/${billId}`,
      token,
      method: "DELETE",
    });
  }

  async bulkInquiryMyBills({
    token,
    billIds,
  }: {
    token?: string;
    billIds: string[];
  }): Promise<
    IGeneralData<{
      total_amount: number;
      results: Array<{
        bill_id: string | number;
        success: boolean;
        message?: string | null;
        data?: unknown;
      }>;
      items: ISavedBill[];
    }>
  > {
    checkIsToken(token);
    return fetcher<
      IGeneralData<{
        total_amount: number;
        results: Array<{
          bill_id: string | number;
          success: boolean;
          message?: string | null;
          data?: unknown;
        }>;
        items: ISavedBill[];
      }>,
      { bill_ids: string[] }
    >({
      url: apiRoutes.core.MY_BILLS_BULK_INQUIRY,
      token,
      method: "POST",
      payload: { bill_ids: billIds },
    });
  }

  async getPurchasedInquiryDetails({
    token,
    purchasedInquiryId,
  }: IGetPurchasedInquiryDetailsParams): Promise<
    IGeneralData<TInquiriesDetailsData>
  > {
    checkIsToken(token);
    const url = `${apiRoutes.core.PURCHASED_INQUIRIES}/${purchasedInquiryId}`;

    const data = await fetcher<IGeneralData<TInquiriesDetailsData>>({
      url,
      token,
    });
    return data;
  }

  async withdrawWalletDeposit({
    token,
    payload,
  }: {
    token: string | undefined;
    payload: IWithdrawWalletDepositPayload;
  }): Promise<IGeneralData<IIncreaseWalletDepositRes>> {
    checkIsToken(token);
    const url = apiRoutes.core.WALLET_WITHDRAW;

    const data = await fetcher<
      IGeneralData<IIncreaseWalletDepositRes>,
      IWithdrawWalletDepositPayload
    >({
      url,
      token,
      payload,
      method: "POST",
    });

    return data;
  }

  async getBanksData(): Promise<IGeneralData<IBanks[]>> {
    const url = apiRoutes.core.BANKS;

    const data = await fetcher<IGeneralData<IBanks[]>>({
      url,
    });
    return data;
  }

  async getPackagesData(params?: {
    token?: string;
    inquiryFlag?: string;
  }): Promise<IGeneralData<IRawPackagesResData>> {
    const url = `${apiRoutes.core.PACKAGES}?flag=${params?.inquiryFlag}`;

    const data = await fetcher<IGeneralData<IRawPackagesResData>>({
      url,
      token: params?.token ? params?.token : undefined,
    });
    return data;
  }

  async purchasePackage({
    token,
    purchasedPackageId,
    bank,
  }: IPurchasePackagePayload): Promise<IGeneralData> {
    checkIsToken(token);
    const url = `${apiRoutes.core.PACKAGES}/${purchasedPackageId}/purchase?bank=${bank}`;

    const data = await fetcher<IGeneralData>({
      url,
      token,
      method: "POST",
    });
    return data;
  }

  async getPurchasedPackages({
    token,
    query,
  }: ITokenBaseQueryParams): Promise<
    IGeneralData<IRawPurchasedPackagesResData>
  > {
    checkIsToken(token);
    const url = apiRoutes.core.PURCHASED_PACKAGES;

    const data = await fetcher<IGeneralData<IRawPurchasedPackagesResData>>({
      url,
      token,
      params: query,
    });
    return data;
  }

  async getInquiryValidity({
    token,
    code,
  }: IInquiryValidityPayload): Promise<
    IGeneralData<IRawInquiryValidityResData>
  > {
    if (token) {
      checkIsToken(token);
    }
    const url = `${apiRoutes.core.INQUIRY_VALIDITY}/${code}`;

    const data = await fetcher<IGeneralData<IRawInquiryValidityResData>>({
      url,
      token,
    });
    return data;
  }

  async getPaidBillDetails({
    token,
    billId,
  }: IGetPaidBillDetailsParams): Promise<IGeneralData<IRawPaidBillResData>> {
    checkIsToken(token);
    const url = `${apiRoutes.core.PAID_BILL}/${billId}`;
    const data = await fetcher<IGeneralData<IRawPaidBillResData>>({
      url,
      token,
    });
    return data;
  }

  async getReceiptDetails({
    token,
    billId,
  }: IGetPaidBillDetailsParams): Promise<IGeneralData<IRawReceiptResData>> {
    checkIsToken(token);
    const url = `${apiRoutes.core.RECEIPT_DETAILS}/${billId}`;
    const data = await fetcher<IGeneralData<IRawReceiptResData>>({
      url,
      token,
    });
    return data;
  }

  async purchaseFile({
    token,
    payload,
  }: {
    token: string;
    payload: any;
  }): Promise<IGeneralData> {
    checkIsToken(token);
    const url = apiRoutes.core.PURCHASE_FILE;

    const data = await fetcher<IGeneralData>({
      url,
      token,
      payload,
      method: "POST",
    });
    return data;
  }
}

export const coreClient = new CoreClient(clientEnv.NEXT_PUBLIC_API_BASE_URL!);
